import type { LeadListItem } from '../types';

import angular from 'angular';
import $ from 'jquery';

import {
  houseApi,
  leadApi,
  projectApi,
  unitApi,
  type UnitStatus,
  userApi,
} from '../services/api';
import { logger } from '../services/logger';
import {
  buildLeadsQueryString,
  type DynamicGroup,
  getDynamicGroups,
  getSidebarOptions,
  MainSectionBuilder,
  normalizeFullTextQuery,
  type SidebarOptions,
} from './queries';

export class LeadsCtrl {
  static $inject = [
    '$timeout',
    '$scope',
    '$cookieStore',
    '$state',
    '$translate',
    'localStorageService',
  ];

  /**
   * Track currently active $timeout
   */
  private declare sortByTimeout: any;
  declare options: SidebarOptions;
  declare dynamicGroups: DynamicGroup[];
  private showOnlyMineWasRemoved = false;
  private offset = 0;
  private limit = 30;

  rawQueries: { param: string[]; main?: string } = {
    param: [],
  };

  query = '';
  lockQuery = false;

  /**
   * Whether we are currently loading leads list
   */
  loadLeads = false;
  showSidebarContent = !$('html').hasClass('-device_tablet');
  dayOfWeekIndex = new Date().getDay();
  showFoucSpinner = false;
  allSmartTasksVisible = false;
  currentUserUsername = '';
  showTxtQuery = false;
  customFieldContructors = {};
  hideSearchLead = true;
  hideSearchMain = true;
  hideSearchNotes = true;
  commonProject = {};
  hideMoreColumns = false;
  fullTextQuery = '';
  userIsAdminRole = false;
  userIsExternalUser = false;
  showMainReservation = false;
  showOnlyMine = false;

  /**
   * Show only Leads that are really considered leads (active leads with at
   * least one opportunity) and not just a contacts
   */
  showOnlyActiveLeads = false;
  sortByAscending = false;
  showSortByAscending = false;
  showMainMore = false;
  lockMainBlockAfterPaste = false;
  isFirstLoad = true;
  queryWasCleared = false;
  scrollLeadLoading = false;

  /**
   * Currently active smart tasks filter preset
   */
  activeGroup = 0;

  leads: LeadListItem[] = [];

  /**
   * Total lead records count for pagination purposes
   */
  total = 0;

  request = {
    fields: ['note', 'unit_info', 'lead_status'],
    sortBy: 'updated_at',
    sortByAscending: false,
    query: null,
    main: {} as { [key: string]: any },
    mains: [] as { [key: string]: any }[],
    statuses: [],
    lead: {} as { [key: string]: any },
    deal: {
      price: {
        type: -1,
        value: 1,
      },
      total: {
        type: -1,
        value: 1,
      },
      pricelist: [],
      payment: [],
    },
    notes: {} as { [key: string]: any },
    tasks: {
      quantity: {
        type: -1,
        value: 1,
      },
      added: {
        type: 1,
        value: 1,
      },
      users: [],
      content: '',
    },
  };

  constructor(
    private $timeout: angular.ITimeoutService,
    private $scope: angular.IScope,
    private $cookieStore,
    private $state,
    private $translate: angular.translate.ITranslateService,
    private localStorageService: angular.local.storage.ILocalStorageService,
  ) {
    if ($cookieStore.get('project')) {
      this.commonProject = $cookieStore.get('project');
    }

    const { request } = this;

    this.options = getSidebarOptions({ $translate });

    const template = MainSectionBuilder.getTemplateObject();
    request.main = template.main;
    request.lead = template.lead;
    request.notes = template.notes;

    request.mains.push(request.main);
    request.mains.push(request.lead);
    request.mains.push(request.notes);

    this.dynamicGroups = getDynamicGroups({
      locale: this.$translate.instant('general.current.lang'),
      isRealtyPro: window.location.origin.includes('realtypro'),
    });
    this.fetchData()
      .then(() => {
        this.initEventHandlers();

        this.buildQuery();

        if (!this.loadLeads) {
          // if this.loadLeads === false this means that buildQuery decided to skip
          // request
          // we won't try to fix legacy logic but just call queryLeads manually
          // in future we need to refactor the whole logic to ensure consistent execution flow
          this.queryLeads();
        }
      })
      .catch((error) => {
        logger.error('Error initializing leads controller', {
          error,
        });
      });
  }

  initEventHandlers() {
    this.$scope.$watch(
      'vm.request.main',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );

    this.$scope.$watch(
      'vm.request.main.what',
      () => {
        let showMainReservation = false;

        this.request.main.what.value.forEach((item) => {
          if (
            item.value === 'Booked' ||
            item.value === 'Extended reservation' ||
            item.value === 'Canceled reservation'
          ) {
            showMainReservation = true;
          }
        });

        this.showMainReservation = showMainReservation;
      },
      true,
    );

    this.$scope.$watch(
      'vm.request.lead',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );
    this.$scope.$watch(
      'vm.request.notes',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );

    this.$scope.$on(
      '$stateChangeSuccess',
      (event, toState, toParams, fromState, _fromParams) => {
        if (
          (fromState.name === 'leads.leadpopup' && toState.name === 'leads') ||
          (fromState.name === 'leads.unitpopup' && toState.name === 'leads') ||
          (fromState.name === 'leads.new' && toState.name === 'leads')
        ) {
          this.offset = 0;
          this.queryLeads();
        }
      },
    );
  }

  private async fetchData() {
    const [
      users,
      groups,
      projects,
      houses,
      statuses,
      tags,
      unitStatuses,
      fields,
    ] = await Promise.all([
      userApi.getAll(),
      userApi.getGroups(),
      projectApi.getAll(),
      houseApi.getAllWithSection(),
      leadApi.getStatuses(),
      leadApi.getTags(),
      unitApi.getStatuses(),
      leadApi.getCustomFields(),
    ]);

    const bookedStatuses: UnitStatus[] = [];

    this.options.unitStatuses = unitStatuses;

    angular.forEach(unitStatuses, (status) => {
      if (status.type === '3') {
        bookedStatuses.push(status);
      }
    });

    this.options.main.reservationTypeOptions = bookedStatuses;

    if (bookedStatuses.length > 0) {
      this.request.main.type = bookedStatuses[0].id;
    }

    users.forEach((item, index) => {
      if (item.current) {
        const [firstUser] = users;

        users[0] = item;

        users[index] = firstUser;

        this.currentUserUsername = item.username;
        this.userIsAdminRole = groups.some(
          (group) => group.admin && item.group_id === group.id,
        );
        this.userIsExternalUser = item.role === userApi.ROLE.TRAINEE;
      }
    });

    this.options.users = users;
    this.options.usersWho = angular.copy(users);
    this.options.usersUpdated = angular.copy(users);
    this.options.usersCreated = angular.copy(users);
    this.options.usersNoteCreated = angular.copy(users);

    if (this.getLsShowOnlyMine() || this.userIsExternalUser) {
      this.showOnlyMine = true;
      this.showOnlyMineChanged(true);
    }

    const sections: {
      name?: string;
      msGroup?: boolean;
    }[] = [];

    houses.forEach((house) => {
      sections.push({ name: house.name, msGroup: true });

      house.sections.forEach((section) => {
        sections.push(angular.copy(section));
      });

      sections.push({ msGroup: false });
    });

    this.options.main.where.section = sections;

    this.processCustomFields(fields);

    this.options.projects = projects.map((project) => ({
      id: project.id,
      name: project.name,
      selected: false,
    }));

    this.options.leadStatuses = statuses.map((status) => {
      return {
        ...status,
        disabled: status.name.includes('--'),
      };
    });

    this.options.itemsOfInterest = tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      selected: false,
    }));

    this.$scope.$apply();
  }

  private buildQuery() {
    const query = buildLeadsQueryString({
      mains: this.request.mains,
      showOnlyMine: this.showOnlyMine,
    });

    const onlyMineRemoved = this.showOnlyMineWasRemoved;

    if (query === this.query && !onlyMineRemoved) {
      return;
    }

    if (query.length) {
      this.fullTextQuery = '';
      this.query = query;
    } else {
      this.query = '';

      if (onlyMineRemoved) {
        this.showOnlyMineWasRemoved = false;
      } else if (this.queryWasCleared) {
        this.queryWasCleared = false;
      } else {
        return;
      }
    }

    this.offset = 0;

    if (this.activeGroup) {
      // triggers queryLeads() under the hood so we won't duplicate that call here
      this.setQueryToGroup(this.activeGroup, true);
    } else {
      this.queryLeads();
    }
  }

  private async queryLeads(
    params: {
      limit?: number;
      offset?: number;

      /**
       * A callback to call on successful request
       *
       * If provided the internal leads assignment will be skipped
       */
      success?: (resp: any) => void;

      /**
       * A callback to call on failed request
       *
       * If provided the internal leads assignment will be skipped
       */
      error?: (error: Error) => void;
    } = {},
  ) {
    if (this.lockQuery) {
      // TODO: this seem to be useless and can be removed in future
      return;
    }

    this.loadLeads = true;
    const { request } = this;

    // Additional columns user turned on
    const fields: string[] = [];

    request.fields.forEach((field) => {
      if (field === 'unit_info' || field === 'lead_status') {
        return;
      }

      fields.push(field);
    });

    try {
      const response = await leadApi.getAll({
        limit: params.limit || 30,
        offset: params.offset || 0,
        query: this.fullTextQuery.length < 4 ? this.query : this.fullTextQuery,
        fields,
        onlyActive: this.showOnlyActiveLeads,
        onlyMine: this.showOnlyMine,
        order_by: request.sortBy,
        order: request.sortByAscending === false ? 'desc' : 'asc',
      });

      this.total = Number(response.total);

      // ensure unit_info sorting
      for (const row of response.rows) {
        row.unit_info?.sort((a, b) => {
          if (a.type === b.type) {
            if (a.active_booking === b.active_booking) {
              // raise items with active booking to the top
              return Number(b.active_booking) - Number(a.active_booking);
            }

            // show newest entries at the top
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );
          }

          // sort in the order of type: booking, purchased, interest
          return Number(a.type) - Number(b.type);
        });
      }

      if (params.success) {
        params.success(response);
      } else {
        this.leads = response.rows;
      }
    } catch (error) {
      logger.error('Error requesting leads', {
        error,
      });

      if (params.error) {
        params.error(error);
      } else {
        this.leads = error;
      }
    }

    this.loadLeads = false;
    this.$scope.$apply();
  }

  filterFullText = () => {
    const normalized = normalizeFullTextQuery({
      raw: this.fullTextQuery,
      isTrainee: this.userIsExternalUser,
    });

    if (normalized === null) {
      return;
    }

    this.fullTextQuery = normalized;
    this.queryLeads();
  };

  isFullTextQueryNumber = () => {
    return /^[0-9]+$/.test(this.fullTextQuery);
  };

  clearFullTextQuery = () => {
    if (this.fullTextQuery.length < 4) {
      this.fullTextQuery = '';
    } else {
      this.fullTextQuery = '';
      this.queryLeads();
    }
  };

  focusSearch = () => {
    this.$timeout(() => {
      angular.element('#b-full-text-query').focus();
    }, 100);
  };

  addProjectToCookie = (project) => {
    let cookieProject = null;

    if (this.$cookieStore.get('project')) {
      cookieProject = this.$cookieStore.get('project');
    }

    if (
      project.selected &&
      this.request.lead.project.value.length === 1 &&
      !cookieProject
    ) {
      this.$cookieStore.put('project', project);
    }
  };

  changeQuery = () => {
    if (this.query && this.query.length > 2) {
      this.queryLeads();
    }
  };

  pasteInSearchQuery = () => {
    this.lockMainBlockAfterPaste = true;
    this.hideSearchMain = true;
  };

  infiniteScrollLoadLeads = () => {
    if (this.scrollLeadLoading) {
      return;
    }

    if (this.leads && this.total === this.leads.length) {
      return;
    }

    if (this.offset >= this.total) {
      return;
    }

    this.scrollLeadLoading = true;

    this.offset += 30;

    this.queryLeads({
      limit: this.limit,
      offset: this.offset,
      success: (leads) => {
        this.leads = this.leads.concat(leads.rows);
        this.scrollLeadLoading = false;
      },
      error: () => {
        this.scrollLeadLoading = false;
      },
    });
  };

  setQueryToGroup = (groupId: number, skipReset = false) => {
    if (this.activeGroup === groupId && !skipReset) {
      this.clearQuery();

      return;
    }

    this.fullTextQuery = '';
    this.activeGroup = groupId;
    this.hideSearchLead = true;
    this.hideSearchMain = true;
    this.hideSearchNotes = true;

    const querySaved = this.dynamicGroups[groupId - 1].query;

    this.query = querySaved;

    this.queryLeads();
  };

  markInMultiSelect = (model, idOrStr) => {
    if (isNaN(idOrStr)) {
      model.forEach((item) => {
        // eslint-disable-next-line eqeqeq
        if (item.value == idOrStr) {
          item.selected = true;
        }
      });
    } else {
      model.forEach((item) => {
        // eslint-disable-next-line eqeqeq
        if (item.id == idOrStr) {
          item.selected = true;
        }
      });
    }
  };

  triggerNewLead = () => {
    if (this.fullTextQuery) {
      this.$state.go('leads.new', { phoneOrEmail: this.fullTextQuery });
    }
  };

  showOnlyMineChanged = (showOnlyMineVal: boolean) => {
    // Marks current user as selected in the "updated by" dropdown (visual) and blocks it
    // for manual interaction
    this.options.usersUpdated[0].selected = showOnlyMineVal;

    if (showOnlyMineVal) {
      this.localStorageService.set('showOnlyMine', true);

      if (this.activeGroup) {
        this.setQueryToGroup(this.activeGroup, true);
      } else {
        // queryLeads should already be called on `this.options.usersUpdated[0].selected` change
        // however we have a logic that skips handling of "userUpdated" inside buildQuery()
        // (because this will introduce redundant filters on backend and break onlyMine feature)
        // therefore we need to trigger manually here
        this.queryLeads();
      }
    } else {
      this.showOnlyMineWasRemoved = true;
      this.localStorageService.remove('showOnlyMine');
    }
  };

  showOnlyActiveLeadsChanged = () => {
    if (this.activeGroup) {
      this.setQueryToGroup(this.activeGroup, true);
    } else {
      this.queryLeads();
    }
  };

  updateRequestModel = (proxyValue, type) => {
    switch (type) {
      case 'leadStatus':
        this.request.lead.leadStatus.value = angular.copy(proxyValue);
        break;
      case 'leadProject':
        this.request.lead.project.value = angular.copy(proxyValue);
        break;
      case 'leadTag':
        this.request.lead.tag.value = angular.copy(proxyValue);
        break;
      case 'leadUserUpdated':
        this.request.lead.userUpdated.value = angular.copy(proxyValue);
        break;
    }
  };

  sortBy = (filter: string) => {
    this.showSortByAscending = true;
    this.sortByAscending = false;

    this.request.sortBy = filter;
    this.request.sortByAscending = this.sortByAscending;

    this.setSortByAscButtonHideTimeout();

    this.queryLeads();
  };

  private setSortByAscButtonHideTimeout() {
    if (this.sortByTimeout) {
      this.$timeout.cancel(this.sortByTimeout);
      this.sortByTimeout = null;
    }

    this.sortByTimeout = this.$timeout(() => {
      this.showSortByAscending = false;
    }, 5000);
  }

  /**
   * For some reason angular ends in infinite loop if one creates new object on each
   * function call and uses it inside template. So we adding reference to
   * re-create object only on change
   */
  private sortingRef?: LeadSorting;

  getSorting = (): LeadSorting => {
    const newSorting: LeadSorting = {
      field: this.request.sortBy,
      order: this.request.sortByAscending ? 'ascend' : 'descend',
    };

    if (!this.sortingRef) {
      this.sortingRef = newSorting;
    }

    if (
      this.sortingRef.field !== newSorting.field ||
      this.sortingRef.order !== newSorting.order
    ) {
      this.sortingRef = newSorting;
    }

    return this.sortingRef;
  };

  onChangeSorting = ({ field, order }: LeadSorting) => {
    this.showSortByAscending = false;

    this.sortByAscending = order === 'ascend';
    this.request.sortBy = field || 'updated_at';
    this.request.sortByAscending = this.sortByAscending;

    this.queryLeads();
  };

  changeSortByAscending = () => {
    this.sortByAscending = !this.sortByAscending;
    this.request.sortByAscending = this.sortByAscending;

    this.setSortByAscButtonHideTimeout();
    this.queryLeads();
  };

  onChangeExtraColumns = (newExtraColumns: string[]) => {
    this.request.fields = newExtraColumns;

    this.queryLeads();
  };

  inputOrKeyUp = ($event, model) => {
    if ($event.keyCode === 27) {
      model.value = null;
      model.enter = false;
    } else if ($event.keyCode === 13) {
      model.enter = true;
      this.buildQuery();
    }
  };

  clearQuery = () => {
    //vm.query = '';
    this.queryWasCleared = true;

    this.request.mains = [];
    const template = angular.copy(MainSectionBuilder.getTemplateObject());
    this.request.main = template.main;
    this.request.lead = template.lead;
    this.request.notes = template.notes;

    this.request.mains.push(this.request.main);
    this.request.mains.push(this.request.lead);
    this.request.mains.push(this.request.notes);

    this.request.lead = angular.extend(
      this.request.lead,
      this.customFieldContructors,
    );

    this.request.sortBy = 'updated_at';
    this.request.sortByAscending = false;

    this.showMainMore = false;
    this.activeGroup = 0;
    this.lockMainBlockAfterPaste = false;
    this.showTxtQuery = false;

    angular.forEach(this.options, (value, key) => {
      if (key === 'columns') {
        // do nothing
      } else if (key === 'customFields') {
        angular.forEach(value, (item) => {
          item.dataMultiSelect.forEach((dataItem) => {
            dataItem.selected = false;
          });
        });
      } else {
        angular.forEach(value, (item) => {
          item.selected = false;
        });
      }
    });

    const deepArrays = [
      this.options.main.where.section!,
      this.options.main.where.type,
      this.options.main.what,
    ];

    deepArrays.forEach((arr) => {
      const tempArr = arr;

      tempArr.forEach((item) => {
        if (!('msGroup' in item)) {
          item.selected = false;
        }
      });

      arr = tempArr;
    });

    if (this.showOnlyMine) {
      this.options.usersUpdated[0].selected = true;
      this.request.lead.userUpdated.value[0] = angular.copy(
        this.options.usersUpdated[0],
      );
    }

    this.buildQuery();
  };

  private processCustomFields(fields) {
    function arrToArrOfObjects(arr: string[]) {
      const newArr: { name: string; disabled: boolean }[] = [];

      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          newArr.push({
            name: item,
            disabled: item.indexOf('--') !== -1,
          });
        });
      }

      return newArr;
    }

    fields.forEach((field) => {
      this.options.columns.push({
        name_translated: field.name,
        value: `custom_field_${field.id}`,
      });

      this.options.customFields.push({
        ...field,
        dataMultiSelect: arrToArrOfObjects(field.data),
      });

      switch (field.typeLabel) {
        case 'Text':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.TextInputOr(`custom_${field.slug}`);
          break;
        case 'Number':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.SelectQuantity(`custom_${field.slug}`);
          break;
        case 'Choice':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.MultiSelect(`custom_${field.slug}`);
          break;
        case 'Date':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.DateFilter(`custom_${field.slug}`);
          break;
      }
    });

    angular.forEach(this.request.lead, (value, key) => {
      if (key.indexOf('custom_') >= 0) {
        this.customFieldContructors[key] = value;
      }
    });
  }

  private getLsShowOnlyMine() {
    if (this.localStorageService.get('showOnlyMine')) {
      return this.localStorageService.get('showOnlyMine');
    }

    return null;
  }
}

export type LeadSorting = {
  field: string | null;
  order: 'ascend' | 'descend' | null;
};
